import { isDeepEqual } from '@/server-data'

import { computeLoadsDelta } from './computeBulkOrderChanges'

import type { BulkLoads, FormData } from '../../types'

type DirtyFields = Record<keyof FormData, boolean>
type DirtyFieldsResult = {
  dirtyFields: DirtyFields
  dirtyCount: number
  dirtyFieldValues: Partial<FormData>
}

export function computeDirtyFields(
  initialValues: FormData,
  currentValues: FormData,
): DirtyFieldsResult {
  const dirtyFields: Record<keyof FormData, boolean> = {} as Record<keyof FormData, boolean>
  const keys = Object.keys(initialValues) as Array<keyof FormData>
  const dirtyFieldValues: Partial<FormData> = {}

  let dirtyCount = 0

  for (const key of keys) {
    const initialField = initialValues[key]
    const currentField = currentValues[key]

    if (key === 'loads') {
      const initialLoads = initialField as BulkLoads
      const currentLoads = currentField as BulkLoads

      const sameExactValues = isDeepEqual(
        computeLoadsDelta(initialLoads),
        computeLoadsDelta(currentLoads),
      )
      const sameLoadNames = isDeepEqual(
        [...Object.keys(initialLoads.exact), ...Object.keys(initialLoads.partial)].sort(),
        [...Object.keys(currentLoads.exact), ...Object.keys(currentLoads.partial)].sort(),
      )

      dirtyFields[key] = !sameExactValues || !sameLoadNames
    } else if (typeof initialField !== 'object' || initialField === null) {
      dirtyFields[key] = initialField !== currentField
    } else if (Array.isArray(initialField)) {
      // Arrays are considered dirty if their length changes or if any item changes
      if (!Array.isArray(currentField)) {
        dirtyFields[key] = true
      } else {
        dirtyFields[key] =
          initialField.length !== currentField.length ||
          initialField.some((item, index) => item !== currentField[index])
      }
    } else {
      // For objects, we do a shallow comparison of their keys and values
      const initialKeys = Object.keys(initialField)
      const currentKeys =
        currentField && typeof currentField === 'object' ? Object.keys(currentField) : []

      if (initialKeys.length !== currentKeys.length) {
        dirtyFields[key] = true
      } else {
        dirtyFields[key] = initialKeys.some(
          k => (initialField as any)[k] !== (currentField as any)[k],
        )
      }
    }

    if (dirtyFields[key]) {
      dirtyCount++
      dirtyFieldValues[key] = currentValues[key] as any
    }
  }

  return { dirtyFields, dirtyCount, dirtyFieldValues }
}
